import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import platformService from '../services/platformService';

const PlatformsScreen = () => {
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingPlatform, setUpdatingPlatform] = useState(null);
  const [syncingPlatform, setSyncingPlatform] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  const loadPlatforms = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await platformService.getPlatforms();
      setPlatforms(data || []);
    } catch (err) {
      console.error('Error al cargar plataformas:', err);
      setError('Error al cargar las plataformas. Por favor, intenta nuevamente.');
      setPlatforms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTourismotionSync = async (platformId) => {
    try {
      setSyncingPlatform(platformId);
      setError('');
      setStatusMessage('');
      const result = await platformService.syncTourismotion();
      setStatusMessage(result.message || 'Sincronización de Tourismotion completada.');
      await loadPlatforms();
    } catch (err) {
      console.error('Error al sincronizar Tourismotion:', err);
      setError(err.response?.data?.message || 'Error al sincronizar Tourismotion.');
    } finally {
      setSyncingPlatform(null);
    }
  };

  useEffect(() => {
    loadPlatforms();
  }, []);

  const handleRefresh = () => {
    loadPlatforms();
  };

  const handleToggleActive = async (platformId, currentActive) => {
    try {
      setUpdatingPlatform(platformId);
      await platformService.updatePlatform(platformId, { active: !currentActive });
      // Actualizar localmente
      setPlatforms(currentPlatforms => currentPlatforms.map(p =>
        p.id === platformId ? { ...p, active: !currentActive } : p
      ));
    } catch (err) {
      console.error('Error al cambiar estado de plataforma:', err);
      setError('Error al actualizar la plataforma. Por favor, intenta nuevamente.');
    } finally {
      setUpdatingPlatform(null);
    }
  };

  const filteredPlatforms = platforms.filter(platform => {
    const searchLower = searchTerm.toLowerCase();
    return (
      platform.name.toLowerCase().includes(searchLower) ||
      platform.description?.toLowerCase().includes(searchLower) ||
      platform.url?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Plataformas disponibles</h1>
        <div className="page-actions">
          <button onClick={handleRefresh} className="btn btn-outline" aria-label="Actualizar plataformas">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      <div className="form-control max-w-md">
        <input
          type="text"
          aria-label="Buscar plataformas"
          placeholder="Buscar plataformas..."
          className="input input-bordered w-full"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {error && (
        <div className="alert alert-error" role="alert">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <div className="sr-only" role="status" aria-live="polite">
        {statusMessage}
      </div>

      {statusMessage && (
        <div className="alert alert-success">
          <span>{statusMessage}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-8">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      ) : (
        <div>
          {filteredPlatforms.length === 0 ? (
            <div className="text-center py-8">
              <p className="page-subtitle">No hay plataformas disponibles en este momento.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPlatforms.map((platform) => (
                <div key={platform.id} className="card bg-base-100 shadow-lg border border-base-200">
                  <div className="card-body flex flex-col h-full">
                    <div className="flex-grow">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-base-content">{platform.name}</h3>
                          <p className="text-sm text-base-content/70">{platform.description}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-4 h-4 rounded-full border border-base-200"
                            style={{ backgroundColor: platform.color }}
                          ></div>
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-1 gap-4 text-sm text-base-content">
                        <div>
                          <span className="font-semibold">Comisión:</span> {platform.commission}%
                        </div>
                        {platform.url && (
                          <div>
                            <a
                              href={platform.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="link link-primary"
                            >
                              Ir al panel
                            </a>
                          </div>
                        )}
                        {(platform.hasApiKey || platform.hasApiCredentials) && (
                          <div>
                            <span className="font-semibold">Acceso API:</span>{' '}
                            {platform.hasApiCredentials ? 'credenciales configuradas' : 'clave configurada'}
                          </div>
                        )}
                        {platform.name.toLowerCase() === 'tourismotion' && platform.lastSchedulerRun && (
                          <div>
                            <span className="font-semibold">Última sincronización:</span>{' '}
                            {new Date(platform.lastSchedulerRun).toLocaleString('es-ES')}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-6 pt-4 border-t border-base-200 flex flex-wrap justify-between items-center gap-3">
                      <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate(`/platforms/edit/${platform.id}`)}
                        className="btn btn-primary btn-sm"
                      >
                        <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Editar
                      </button>
                      {platform.name.toLowerCase() === 'tourismotion' && (
                        <button
                          type="button"
                          onClick={() => handleTourismotionSync(platform.id)}
                          className="btn btn-outline btn-sm"
                          disabled={!platform.active || !platform.hasApiCredentials || syncingPlatform === platform.id}
                          aria-describedby={`tourismotion-sync-help-${platform.id}`}
                        >
                          {syncingPlatform === platform.id ? (
                            <span className="loading loading-spinner loading-xs" aria-hidden="true"></span>
                          ) : null}
                          {syncingPlatform === platform.id ? 'Sincronizando' : 'Sincronizar'}
                        </button>
                      )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-base-content/70">
                          {platform.active ? 'Activa' : 'Inactiva'}
                        </span>
                        <input
                          type="checkbox"
                          className="toggle toggle-primary"
                          checked={platform.active}
                          onChange={() => handleToggleActive(platform.id, platform.active)}
                          disabled={updatingPlatform === platform.id}
                          aria-label={`${platform.active ? 'Desactivar' : 'Activar'} ${platform.name}`}
                        />
                      </div>
                      {platform.name.toLowerCase() === 'tourismotion' && (
                        <span id={`tourismotion-sync-help-${platform.id}`} className="sr-only">
                          {!platform.active
                            ? 'Activa la plataforma antes de sincronizar.'
                            : !platform.hasApiCredentials
                              ? 'Configura el email y la contraseña antes de sincronizar.'
                              : 'Importa y actualiza las reservas de Tourismotion.'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlatformsScreen; 
